import { Terminal } from "lucide-react";
import type { ReactNode } from "react";
import { inferredPlatform } from "@/components/devices/device-display";
import type { DeviceWorkerItem } from "@/lib/api/connections";

/**
 * First-party connector runtimes: the Mac app, Chrome extension, and terminal
 * or Docker variants of the headless worker. External agents and MCP clients
 * live under Agents.
 *
 * These are static — there is no table behind them and nothing to fetch. They
 * are on the connectors surface because that is where someone goes to ask
 * "what can my agents reach?", and the honest answer includes "your laptop,
 * once you install this". Previously they lived on a standalone Devices page
 * that you could only reach from a user-menu dropdown, so the answer was
 * discoverable only if you already knew it.
 *
 * Each installed family appears once in the inventory. Its detail lists the
 * workspace-scoped paired profiles/devices and setup for additional devices.
 */

/** One instruction: a command to run, or a place to go. */
export interface ConnectorAppStep {
	label: string;
	/** Shell command, rendered as a copyable block. */
	command?: string;
	/** External destination — a store listing, a download, a booking link. */
	href?: string;
}

export interface ConnectorAppEntry {
	/** Route segment: `/connectors/app/{id}`. */
	id: string;
	name: string;
	icon: ReactNode;
	/** The list row's second line — what it is, in a few words. */
	tagline: string;
	/** The detail page's caption — what it does once connected. */
	description: string;
	steps: ConnectorAppStep[];
	/**
	 * Platforms a paired device reports when this app is what paired it. A
	 * workspace that already has one stops being offered the app on the list;
	 * the catalog still carries it, for a second machine.
	 */
	devicePlatforms?: readonly string[];
}

const MAC_DOWNLOAD_URL =
	"https://github.com/lobu-ai/lobu/releases/latest/download/Lobu.dmg";
const CHROME_STORE_URL =
	"https://chromewebstore.google.com/detail/jhgcecbdpnoehfnhpdfihlchjddapepi";
function dockerWorkerCommand(installationOrigin: string): string {
	const apiUrl = new URL(installationOrigin);
	if (["127.0.0.1", "localhost", "[::1]"].includes(apiUrl.hostname)) {
		apiUrl.hostname = "host.docker.internal";
	}
	// The image pre-creates `/home/worker` for uid 1001. Mount the whole home so
	// Docker seeds a fresh named volume with that ownership; mounting the absent
	// .config/lobu leaf can instead leave the non-root CLI unable to save login.
	return `docker run --rm --name lobu-worker --interactive --tty \\
  --add-host host.docker.internal:host-gateway \\
  --volume lobu-worker-state:/home/worker \\
  ghcr.io/lobu-ai/lobu-worker:latest \\
  npx -y @lobu/cli@latest daemon --api-url ${apiUrl.origin}`;
}

function brandIcon(
	d: string,
	options?: { fill?: string; className?: string },
): ReactNode {
	return (
		<svg
			aria-hidden="true"
			className={`h-4 w-4 shrink-0 ${options?.className ?? ""}`}
			viewBox="0 0 24 24"
			fill={options?.fill ?? "currentColor"}
		>
			<path d={d} />
		</svg>
	);
}

const APPLE_ICON = brandIcon(
	"M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z",
	// Apple is monochrome by brand — full-contrast foreground instead of the
	// muted row tint, so it reads as a logo rather than a glyph.
	{ className: "text-foreground" },
);
const CHROME_ICON: ReactNode = (
	<svg aria-hidden="true" className="h-4 w-4 shrink-0" viewBox="0 0 48 48">
		<path
			fill="#EA4335"
			d="M 7.42 12.82 A 20 20 0 0 1 40.58 12.82 L 33.95 17.29 A 12 12 0 0 0 14.05 17.29 Z"
		/>
		<path
			fill="#34A853"
			d="M 41.98 15.23 A 20 20 0 0 1 25.40 43.95 L 24.84 35.97 A 12 12 0 0 0 34.79 18.74 Z"
		/>
		<path
			fill="#FBBC05"
			d="M 22.60 43.95 A 20 20 0 0 1 6.02 15.23 L 13.21 18.74 A 12 12 0 0 0 23.16 35.97 Z"
		/>
		<circle cx="24" cy="24" r="12" fill="#FFFFFF" />
		<circle cx="24" cy="24" r="8.5" fill="#1A73E8" />
	</svg>
);

const DOCKER_ICON = brandIcon(
	"M13.983 11.078h2.119a.186.186 0 0 0 .186-.185V9.006a.186.186 0 0 0-.186-.186h-2.119a.185.185 0 0 0-.185.185v1.888c0 .102.083.185.185.185m-2.954-5.43h2.118a.186.186 0 0 0 .186-.186V3.574a.186.186 0 0 0-.186-.185h-2.118a.185.185 0 0 0-.185.185v1.888c0 .102.082.185.185.186m0 2.716h2.118a.187.187 0 0 0 .186-.186V6.29a.186.186 0 0 0-.186-.185h-2.118a.185.185 0 0 0-.185.185v1.887c0 .102.082.185.185.186m-2.93 0h2.12a.186.186 0 0 0 .184-.186V6.29a.185.185 0 0 0-.185-.185H8.1a.185.185 0 0 0-.185.185v1.887c0 .102.083.185.185.186m-2.964 0h2.119a.186.186 0 0 0 .185-.186V6.29a.185.185 0 0 0-.185-.185H5.136a.186.186 0 0 0-.186.185v1.887c0 .102.084.185.186.186m5.893 2.715h2.118a.186.186 0 0 0 .186-.185V9.006a.186.186 0 0 0-.186-.186h-2.118a.185.185 0 0 0-.185.185v1.888c0 .102.082.185.185.185m-2.93 0h2.12a.185.185 0 0 0 .184-.185V9.006a.185.185 0 0 0-.184-.186h-2.12a.185.185 0 0 0-.184.185v1.888c0 .102.083.185.185.185m-2.964 0h2.119a.185.185 0 0 0 .185-.185V9.006a.185.185 0 0 0-.184-.186h-2.12a.186.186 0 0 0-.186.186v1.887c0 .102.084.185.186.185m-2.92 0h2.12a.185.185 0 0 0 .184-.185V9.006a.185.185 0 0 0-.184-.186h-2.12a.185.185 0 0 0-.184.185v1.888c0 .102.082.185.185.185M23.763 9.89c-.065-.051-.672-.51-1.954-.51-.338.001-.676.03-1.01.087-.248-1.7-1.653-2.53-1.716-2.566l-.344-.199-.226.327c-.284.438-.49.922-.612 1.43-.23.97-.09 1.882.403 2.661-.595.332-1.55.413-1.744.42H.751a.751.751 0 0 0-.75.748 11.376 11.376 0 0 0 .692 4.062c.545 1.428 1.355 2.48 2.41 3.124 1.18.723 3.1 1.137 5.275 1.137a16.094 16.094 0 0 0 2.913-.262 12.27 12.27 0 0 0 3.805-1.38c.965-.582 1.832-1.31 2.572-2.161 1.236-1.418 1.974-3.012 2.521-4.426h.097c1.71 0 2.616-.823 2.65-1.74l.005-.184Z",
	{ fill: "#0DB7ED" },
);

/**
 * The catalog for one workspace and Lobu installation. The daemon command is
 * installation-scoped because device access to a team workspace is established
 * by a pinned connection or Automation, not by a CLI `--org` flag.
 */
export function buildConnectorApps(
	_ownerSlug: string,
	installationOrigin: string,
): ConnectorAppEntry[] {
	return [
		{
			id: "owletto-mac",
			name: "Mac Application",
			icon: APPLE_ICON,
			tagline: "Menu bar app for macOS",
			description:
				"Runs agents on your Mac and, with the relevant permissions, brings selected local context such as Screen Time and calendars into this workspace.",
			devicePlatforms: ["macos"],
			steps: [{ label: "Download for Mac", href: MAC_DOWNLOAD_URL }],
		},
		{
			id: "owletto-chrome",
			name: "Chrome",
			icon: CHROME_ICON,
			tagline: "Browser extension",
			description:
				"Lets agents read the page you are on and act in your logged-in browser session, and pairs this workspace for one-click connector auth.",
			devicePlatforms: ["chrome-extension"],
			steps: [{ label: "Add to Chrome", href: CHROME_STORE_URL }],
		},
		{
			id: "lobu-cli",
			name: "Lobu CLI",
			icon: <Terminal className="h-4 w-4 shrink-0" />,
			tagline: "Run on this machine",
			description:
				"Runs connector syncs, actions, and Automations as a headless worker on this machine. The first run opens this Lobu installation's login flow; no API token or organization flag is needed.",
			devicePlatforms: ["headless"],
			steps: [
				{
					label: "Run the daemon",
					command: `npx -y @lobu/cli@latest daemon --api-url ${installationOrigin}`,
				},
			],
		},
		{
			id: "lobu-docker",
			name: "Docker",
			icon: DOCKER_ICON,
			tagline: "Run on a server or VPS",
			description:
				"Runs the same headless worker in Docker for connectors that need a persistent, always-on environment. The first run uses this Lobu installation's login flow and stores the resulting device credential in the named volume.",
			devicePlatforms: ["headless"],
			steps: [
				{
					label: "Run the worker",
					command: dockerWorkerCommand(installationOrigin),
				},
			],
		},
	];
}

export function findConnectorApp(
	ownerSlug: string,
	appId: string,
	installationOrigin: string,
): ConnectorAppEntry | undefined {
	// Keep old bookmarked detail URLs working after the Docker option stopped
	// using the retired "Lobu bridge" product name.
	const canonicalAppId = appId === "lobu-bridge" ? "lobu-docker" : appId;
	return buildConnectorApps(ownerSlug, installationOrigin).find(
		(app) => app.id === canonicalAppId,
	);
}

/**
 * The apps worth offering on the connectors list: everything not already
 * answered by a paired device. Terminal and Docker are two installation
 * methods for the same `headless` platform, so both disappear once either one
 * has paired a worker; the full Add catalog keeps both available.
 */
export function offerableConnectorApps(
	ownerSlug: string,
	devices: readonly DeviceWorkerItem[],
	installationOrigin: string,
): ConnectorAppEntry[] {
	const platforms = new Set(
		devices.map((device) => inferredPlatform(device)).filter(Boolean),
	);
	return buildConnectorApps(ownerSlug, installationOrigin).filter(
		(app) => !app.devicePlatforms?.some((platform) => platforms.has(platform)),
	);
}
